import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';

import { blastByNameQueryOptions, blastLimitsQueryOptions, optimiserByBlastQueryOptions } from '~api/queries';
import { EmptyState, Loader } from '~components';
import { ROUTE_IDS } from '~config/constants';
import { useFilteredOptimisations } from '~features/blast-optimisations';
import type { ColourByParameter, OptimisationDrillHoleProperties } from '~types';
import { OptimiserDrillHoleCanvas, OptimiserDrillHoleDetails, OptimiserSidebar } from '../components';
import { PARAMETERS_LABEL_COLOURS } from '../config';

/**
 * Blast Optimisation Viewer page — displays the blast optimisation pattern design for a specific blast and optimisation,
 * along with drill hole details and hardness zones.
 */
const BlastOptimisationViewer = () => {
  const navigate = useNavigate();
  const [containerRef, setContainerRef] = useState<HTMLElement | null>(null);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [colourParameter, setColourParameter] = useState<ColourByParameter>('depth');
  const [selectedDrillHole, setSelectedDrillHole] = useState<OptimisationDrillHoleProperties | null>(null);
  const { optimiserId, blastName } = useParams({ strict: false });

  // Read blast data from the query cache — guaranteed by the route loader's ensureQueryData.
  // This is stable across sibling route transitions (no store cleanup race condition).
  const { data: selectedBlast } = useQuery({
    ...blastByNameQueryOptions(blastName as string),
    enabled: !!blastName,
  });

  const {
    data: blastOptimisations,
    isLoading: loadingBlast,
    isError,
    error,
  } = useQuery({
    ...optimiserByBlastQueryOptions(blastName as string),
    enabled: !!blastName,
  });

  const { filteredOptimisations } = useFilteredOptimisations(blastOptimisations?.optimisations ?? []);

  const { data: blastLimitsData, isLoading: loadingBlastLimits } = useQuery({
    ...blastLimitsQueryOptions(blastName as string),
    enabled: !!blastName,
  });

  // Find the specific optimisation
  const optimisation = useMemo(() => {
    if (!optimiserId || !blastOptimisations?.optimisations) return null;
    return blastOptimisations.optimisations.find((opt) => Number(opt.optimisation_id) === Number(optimiserId)) ?? null;
  }, [optimiserId, blastOptimisations]);

  // Derive base colour for the selected parameter
  const colour = useMemo(
    () => PARAMETERS_LABEL_COLOURS.find((p) => p.value === colourParameter)?.colour,
    [colourParameter],
  );

  const handleDrillHoleClick = useCallback((hole: OptimisationDrillHoleProperties) => {
    setSelectedDrillHole(hole);
    setDrawerOpen(true);
  }, []);

  const handleSelectedOptimisationChange = (optimisationId: number) => {
    clearSelection();
    // By navigating to the new selected optimisation, the route will update and re-fetch the data accordingly
    navigate({
      to: ROUTE_IDS.BLAST_OPTIMISATION_VIEWER,
      params: { blastName: blastName as string, optimiserId: String(optimisationId) },
    });
  };

  const handleColourParameterChange = (property: ColourByParameter) => {
    clearSelection();
    setColourParameter(property);
  };

  const handleDrawerClose = () => {
    clearSelection();
  };

  const clearSelection = () => {
    setSelectedDrillHole(null);
    setDrawerOpen(false);
  };

  // Error states
  if (!blastName) {
    return <EmptyState title="No blast selected" description="Please select a blast to view its details." />;
  }

  if (isError) {
    return <EmptyState title="Error loading data" description={error?.message || 'Unknown error'} />;
  }

  if (loadingBlast || loadingBlastLimits) {
    return (
      <div className="grid h-full w-full place-items-center">
        <Loader orientation="vertical">Loading optimiser data...</Loader>
      </div>
    );
  }

  if (!blastOptimisations) {
    return <EmptyState title="No data available" description="Blast data could not be loaded." />;
  }

  if (!optimisation) {
    return <EmptyState title="Optimisation not found" description="The requested optimisation could not be found." />;
  }

  return (
    <div className="relative flex h-full w-full flex-col">
      <div
        className="relative flex min-h-0 flex-1 overflow-clip [--optimiser-sidebar-width:400px]"
        ref={setContainerRef}
      >
        <OptimiserSidebar
          allOptimisations={filteredOptimisations}
          selectedColourByParameter={colourParameter}
          selectedOptimisationId={optimisation.optimisation_id}
          onSelectedOptimisationChange={handleSelectedOptimisationChange}
          onColourParameterChange={handleColourParameterChange}
        />
        <OptimiserDrillHoleCanvas
          blastPolygon={selectedBlast?.polygon}
          colour={colour}
          colourByParameter={colourParameter}
          selectedDrillHoleId={selectedDrillHole?.hole_name ?? null}
          onDrillHoleClick={handleDrillHoleClick}
          optimisation={optimisation}
          blastLimits={blastLimitsData}
        />
        {containerRef && (
          <OptimiserDrillHoleDetails
            container={containerRef}
            open={drawerOpen}
            onClose={handleDrawerClose}
            drillHoleData={selectedDrillHole}
          />
        )}
      </div>
    </div>
  );
};

export { BlastOptimisationViewer };
